import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import React, { FC, useRef } from "react";
import SwiperCore from "swiper";
// import "swiper/css";
// import "swiper/css/autoplay";
// import "swiper/css/navigation";
// import "swiper/css/pagination";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { pushCarouselClick } from "@/dataLayer/dataLayer";

import { CardWidthModal } from "./VerticalCardSlideShow";

interface Props {
  card: CardWidthModal | null;
  onClose: () => void;
}

SwiperCore.use([Autoplay, Pagination, Navigation]);

const ModalSwiper: FC<Props> = ({ card, onClose }) => {
  const locale = useLocale();
  const [language] = locale.split("-");
  const t = useTranslations();
  const arrowLeft = useRef<HTMLButtonElement>(null);
  const arrowRight = useRef<HTMLButtonElement>(null);
  const slider = useRef<SwiperRef>(null);

  const pathname = usePathname();
  const strippedPathname = pathname?.replace(/^\/[^/]+/, "");
  const pageName = strippedPathname === "/" ? "home" : strippedPathname;

  if (!card) {
    return <></>;
  }

  return (
    <>
      <div className="flex flex-col w-full">
        <div className="grow-0 px-m mb-xl rtl:md:mr-[21.5%] ltr:md:ml-[21.5%] md:max-w-5xl md:mb-l">
          <div className="flex flex-col md:flex-row md:items-center mt-xl md:mt-0">
            <Image className="mb-l md:mb-0 w-[120px]" src={card.logo.url} alt={card.logo.title} width={100} height={80} />
            <div>
              <div className={language === "ar" ? "text-right rtl:md:pr-l" : "text-left ltr:md:pl-l"}>
                <h3 className="font-secondary uppercase text-s md:mb-s">{card.modalTitle}</h3>
                <p className="text-s">{card.modalDescription}</p>
              </div>
            </div>
            <Button
              customClass="block my-l w-full md:!hidden text-center"
              size={ButtonSize.SMALL}
              theme={ButtonTheme.BLACK}
              type="button"
              variant={ButtonVariant.SECONDARY}
            >
              {t("project.view.specific", { project: card.logo.title })}
            </Button>
          </div>
        </div>

        <div className="grow px-m md:hidden">
          {card.modalGallery?.map((item, index) => (
            <Image key={index} className="w-full h-auto rounded-lg mb-l" src={item.url} alt={item.title} width={1500} height={550} />
          ))}
        </div>
        <div className="grow hidden md:flex flex-col">
          <Swiper
            ref={slider}
            slidesPerView={1.8}
            spaceBetween={20}
            height={200}
            centeredSlides={true}
            enabled={false}
            loop={true}
            navigation={{
              nextEl: `.vertical-wall-modal-next`,
              prevEl: `.vertical-wall-modal-prev`,
            }}
            breakpoints={{
              1024: {
                enabled: true,
              },
            }}
            pagination={{ type: "bullets", el: ".swiper-pagination-modal", clickable: true }}
            className="w-full grow relative"
          >
            {card.modalGallery?.map((item, index) => (
              <SwiperSlide className="flex items-center justify-center md:max-h-[69vh] rounded-lg overflow-hidden" key={index}>
                <Image className="h-full w-auto mx-auto object-cover" src={item.url} alt={item.title} width={1600} height={1060} />
              </SwiperSlide>
            ))}
            <button
              ref={arrowLeft}
              className="absolute left-[20%] top-1/2 z-10 bg-white w-xxl aspect-1-1 rounded-full flex items-center justify-center vertical-wall-modal-prev"
              onClick={() => pushCarouselClick("carousel_click", card.type, pageName, card.modalTitle)}
            >
              <Icon className="stroke-black" name="arrow-left" size="20" />
            </button>
            <button
              className="absolute left-[77%] top-1/2 z-10 bg-white w-xxl aspect-1-1 rounded-full flex items-center justify-center  vertical-wall-modal-next"
              onClick={() => pushCarouselClick("carousel_click", card.type, pageName, card.modalTitle)}
            >
              <Icon className="stroke-black" name="arrow-right" size="20" />
            </button>
          </Swiper>

          <div className="grow-0">
            <div className="custom-modal-swiper swiper-pagination-modal mt-m"></div>
            {card.modalCTA && (
              <Button
                href={getLink(card.modalCTA, locale)}
                customClass="!block w-fit mt-l mx-auto"
                size={ButtonSize.SMALL}
                theme={ButtonTheme.BLACK}
                type="button"
                variant={ButtonVariant.SECONDARY}
              >
                {card.modalCTA?.label}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalSwiper;
